<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import debounce from 'lodash/debounce';
import { DashboardWidget } from '@/interfaces/Dashboard';
import ConnxSkeletonExpiredLicencesCount from './ConnxSkeletonExpiredLicencesCount.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

const earlyExitRate = ref<number | null>(0);
const newHiresThisMonth = ref<number | null>(0);
const earlyExitsThisMonth = ref<number | null>(0);
const avgTenure = ref<number | null>(0);
const loading = ref(true);

const dashboardStore = useDashboardStore();

const fetchData = async () => {
  try {
    const response = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });

    if (response?.data) {
      const mappedData = Object.fromEntries(
        (response.data as { name: string; data: string }[]).map((item) => [item.name, item.data])
      ) as Record<string, string>;

      // Parse numbers safely
      newHiresThisMonth.value = parseInt(mappedData.newHiresThisMonth) || 0;
      earlyExitRate.value = parseFloat(mappedData.earlyExitRate) || 0;
      earlyExitsThisMonth.value = parseInt(mappedData.earlyExitsThisMonth) || 0;

      // remove "months" text before parsing
      avgTenure.value = parseFloat(mappedData.avgTenure.replace(/[^\d.]/g, '')) || 0;
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  } finally {
    loading.value = false;
  }
};

const statusClasses: Record<
  string,
  {
    border: string;
    text: string;
    bgTint: string;
    background: string;
  }
> = {
  success: {
    border: 'tw-border-connx-success',
    text: 'tw-text-connx-success',
    bgTint: 'tw-bg-connx-success-tint-95',
    background: 'tw-bg-connx-success',
  },
  warning: {
    border: 'tw-border-connx-warning',
    text: 'tw-text-connx-warning',
    bgTint: 'tw-bg-connx-warning-tint-95',
    background: 'tw-bg-connx-warning',
  },
  danger: {
    border: 'tw-border-connx-danger',
    text: 'tw-text-connx-danger',
    bgTint: 'tw-bg-connx-danger-tint-95',
    background: 'tw-bg-connx-danger',
  },
  info: {
    border: 'tw-border-connx-info',
    text: 'tw-text-connx-info',
    bgTint: 'tw-bg-connx-info-tint-95',
    background: 'tw-bg-connx-info',
  },
  primary: {
    border: 'tw-border-connx-primary',
    text: 'tw-text-connx-primary',
    bgTint: 'tw-bg-connx-primary-tint-95',
    background: 'tw-bg-connx-primary',
  },
};

const showExceptEarlyExite = computed(() => {
  if (!props.widget) return false;
  if (props.widget.defaultJsonProperties.h <= 2 && props.widget.defaultJsonProperties.w <= 3) {
    return false;
  }
  return true;
});

const squares = computed(() => {
  let earlyExitRateStatus = 'primary';
  const [benchmarkFirst, benchmarkSecond] = props.widget.defaultJsonProperties?.parameters?.benchmarks?.values ?? [
    0, 0,
  ];

  if (earlyExitRate.value! <= benchmarkFirst) {
    earlyExitRateStatus = 'success';
  }
  if (earlyExitRate.value! > benchmarkFirst && earlyExitRate.value! <= benchmarkSecond) {
    earlyExitRateStatus = 'warning';
  }
  if (earlyExitRate.value! > benchmarkSecond) {
    earlyExitRateStatus = 'danger';
  }

  return [
    { label: 'Early Exit %', value: `${earlyExitRate.value}`, status: earlyExitRateStatus, isEarlyExit: true },
    {
      label: 'Average Tenure (Months)',
      value: avgTenure.value,
      subtitle: 'months',
      status: 'primary',
      isEarlyExit: false,
    },
    { label: 'New Hires This Month', value: newHiresThisMonth.value, status: 'primary', isEarlyExit: false },
    { label: 'Early Exits This Month', value: earlyExitsThisMonth.value, status: 'primary', isEarlyExit: false },
  ];
});

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;

    return {
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
    };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);

onMounted(fetchData);
</script>

<template>
  <ConnxSkeletonExpiredLicencesCount v-if="loading" />
  <template v-else>
    <div class="tw-w-full tw-h-full">
      <div class="tw-h-full tw-grid tw-grid-cols-2 tw-gap-4">
        <template v-for="square in squares" :key="square.label">
          <div
            v-if="square.isEarlyExit || showExceptEarlyExite"
            :class="[statusClasses[square.status].border, statusClasses[square.status].bgTint]"
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-border-2 tw-border-solid tw-rounded-lg tw-px-4 tw-py-2 tw-flex-1 tw-text-center"
          >
            <!-- Value -->
            <p class="tw-text-3xl tw-font-bold tw-mb-2" :class="statusClasses[square.status].text">
              {{ square.value }}
            </p>

            <!-- Subtitle above label (only for tenure box) -->
            <span v-if="square.subtitle" class="tw-text-sm tw-mb-1" :class="statusClasses[square.status].text">
              {{ square.subtitle }}
            </span>

            <!-- Label pill -->
            <span
              class="tw-text-connx-white tw-text-sm tw-font-medium tw-px-4 tw-py-1 tw-rounded-full tw-text-center"
              :class="statusClasses[square.status].background"
            >
              {{ square.label }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <ConnxDashboardWidgetNoData v-if="!earlyExitRate && !newHiresThisMonth && !earlyExitsThisMonth && !avgTenure" />
  </template>
</template>
